import type { CtaActionType, CtaBlock, CtaButtonStyle, CtaIntent } from '@/types/cta';

const CTA_BLOCK_REGEX = /(^|\n):::cta\s*\n([\s\S]*?)\n:::(?=\n|$)/g;

export type ContentSegment =
  | { type: 'markdown'; content: string }
  | { type: 'cta'; cta: CtaBlock };

function decodeHtmlAttribute(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

function encodeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function normaliseActionType(value: string | undefined): CtaActionType {
  return value === 'url' || value === 'email' ? value : 'form';
}

function normaliseIntent(value: string | undefined): CtaIntent | undefined {
  return value === 'demo' || value === 'callback' || value === 'contact' ? value : undefined;
}

function normaliseButtonStyle(value: string | undefined): CtaButtonStyle {
  return value === 'secondary' ? 'secondary' : 'primary';
}

export function extractCtaFields(raw: string): CtaBlock | null {
  const fields: Record<string, string> = {};

  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const colonIndex = trimmed.indexOf(':');
    if (colonIndex <= 0) continue;
    const key = trimmed.slice(0, colonIndex).trim();
    const value = trimmed.slice(colonIndex + 1).trim();
    fields[key] = value;
  }

  if (!fields['headline'] || !fields['actionLabel']) return null;

  const actionType = normaliseActionType(fields['actionType']);
  const cta: CtaBlock = {
    headline: fields['headline'],
    body: fields['body'],
    actionLabel: fields['actionLabel'],
    actionType,
    intent: normaliseIntent(fields['intent']),
    buttonStyle: normaliseButtonStyle(fields['buttonStyle']),
  };

  if (actionType === 'url' && fields['href']) cta.href = fields['href'];
  if (actionType === 'email' && fields['email']) cta.email = fields['email'];
  if (actionType === 'form' && fields['emailVar']) cta.emailVar = fields['emailVar'];

  return cta;
}

export function parseCtaDirective(markdown: string): string {
  return markdown.replace(CTA_BLOCK_REGEX, (fullMatch, leadingNewline, rawFields) => {
    const cta = extractCtaFields(rawFields);
    if (!cta) return fullMatch;

    const attrs = ([
      ['headline', cta.headline],
      ['body', cta.body ?? ''],
      ['actionlabel', cta.actionLabel],
      ['actiontype', cta.actionType],
      ['intent', cta.intent ?? ''],
      ['href', cta.href ?? ''],
      ['email', cta.email ?? ''],
      ['buttonstyle', cta.buttonStyle ?? 'primary'],
      ['emailvar', cta.emailVar ?? ''],
    ] as const)
      .map(([key, value]) => `${key}="${encodeHtmlAttribute(value)}"`)
      .join(' ');

    return `${leadingNewline}<cta-block ${attrs}></cta-block>`;
  });
}

export function splitContentWithCtas(markdown: string): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let lastIndex = 0;

  for (const match of markdown.matchAll(CTA_BLOCK_REGEX)) {
    const matchIndex = match.index ?? 0;
    const leadingNewline = match[1] ?? '';
    const rawFields = match[2] ?? '';
    const fullMatch = match[0];
    const cta = extractCtaFields(rawFields);

    const markdownBefore = markdown.slice(lastIndex, matchIndex) + leadingNewline;
    if (markdownBefore.trim()) {
      segments.push({ type: 'markdown', content: markdownBefore.trim() });
    }

    if (cta) {
      segments.push({ type: 'cta', cta });
    } else if (fullMatch.trim()) {
      segments.push({ type: 'markdown', content: fullMatch.trim() });
    }

    lastIndex = matchIndex + fullMatch.length;
  }

  const tail = markdown.slice(lastIndex);
  if (tail.trim()) {
    segments.push({ type: 'markdown', content: tail.trim() });
  }

  return segments.length > 0 ? segments : [{ type: 'markdown', content: markdown }];
}

export function parseMdxBody(raw: string): string {
  const match = raw.match(/^---\r?\n[\s\S]*?\r?\n---\r?\n?([\s\S]*)$/);
  return match?.[1]?.trim() ?? raw.trim();
}

export function readCtaAttributes(
  attributes: Record<string, string | undefined>,
): CtaBlock | null {
  const headline = attributes['headline'] ? decodeHtmlAttribute(attributes['headline']) : '';
  const actionLabel = attributes['actionlabel']
    ? decodeHtmlAttribute(attributes['actionlabel'])
    : '';

  if (!headline || !actionLabel) return null;

  const actionType = normaliseActionType(attributes['actiontype']);
  const body = attributes['body'] ? decodeHtmlAttribute(attributes['body']) : undefined;
  const intent = normaliseIntent(attributes['intent']);
  const href = attributes['href'] ? decodeHtmlAttribute(attributes['href']) : undefined;
  const email = attributes['email'] ? decodeHtmlAttribute(attributes['email']) : undefined;
  const emailVar = attributes['emailvar'] ? decodeHtmlAttribute(attributes['emailvar']) : undefined;

  return {
    headline,
    body,
    actionLabel,
    actionType,
    intent,
    href,
    email,
    emailVar,
    buttonStyle: normaliseButtonStyle(attributes['buttonstyle']),
  };
}

export function buildCtaHref(
  cta: CtaBlock,
  context?: { slug?: string; title?: string },
): string {
  if (cta.actionType === 'url' && cta.href) return cta.href;
  if (cta.actionType === 'email') {
    const email = cta.email?.trim();
    return email ? `mailto:${email}` : '/contact';
  }

  const params = new URLSearchParams();
  if (cta.intent) params.set('intent', cta.intent);
  if (cta.emailVar) params.set('emailVar', cta.emailVar);
  if (context?.slug) params.set('sourcePost', context.slug);
  if (context?.title) params.set('sourceTitle', context.title);

  const query = params.toString();
  return query ? `/contact?${query}` : '/contact';
}
